import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useAvatarUrl } from "@/lib/avatars/useAvatarUrl";

interface UserAvatarProps {
  /** Storage path in the private `avatars` bucket (e.g. `{user_id}/avatar-123.webp`). */
  path?: string | null;
  name?: string | null;
  email?: string | null;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const sizeClasses: Record<NonNullable<UserAvatarProps["size"]>, string> = {
  sm: "h-6 w-6 text-[10px]",
  md: "h-9 w-9 text-xs",
  lg: "h-20 w-20 text-lg",
};

function getInitials(name?: string | null, email?: string | null): string {
  const source = (name || email || "").trim();
  if (!source) return "U";
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * User avatar component.
 *
 * PRIVACY: Use ONLY in the current user's own account chrome (top-right
 * dropdown, Profile/Settings). Do NOT mount inside any LR Direct bid /
 * quote / broker-facing or counterparty view — that would leak identity
 * in the blind-bid model.
 *
 * The `avatars` bucket is private; pass the storage `path` and this
 * component will mint a short-lived signed URL at render time.
 */
export function UserAvatar({ path, name, email, size = "md", className }: UserAvatarProps) {
  const initials = getInitials(name, email);
  const { url } = useAvatarUrl(path);
  return (
    <Avatar className={cn(sizeClasses[size], className)}>
      {url ? <AvatarImage src={url} alt={name || email || "User avatar"} /> : null}
      <AvatarFallback className="bg-brand-teal-deep text-white font-bold">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

export default UserAvatar;
